/**
 * Weather store.
 *
 * Two slots, populated independently:
 *   - `allStations` — full broker snapshot of every ground station
 *     (~200 rows; MSC + Alberta511 + Netatmo). Populated by the MQTT
 *     `weather/sensors` topic via MqttSourceLegacy. Empty in mock mode
 *     (mock keeps the REST `/api/weather` path).
 *   - `sensors` — the small "near this drone" projection the Weather
 *     tab pushes via `setSensors`. Map layer reads from here.
 *
 * `allStations` is the raw source; consumers project to whatever shape
 * they need (distance-filtered, source-filtered, etc.).
 */
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { WxSensorRecord } from '@/lib/mqtt/schemas/weather';

/* ---- Last-known snapshot ------------------------------------------------
 * `weather/sensors` is not retained and each source is published about once a
 * minute (MSC ~65 s apart), so a fresh page load waited up to a minute for the
 * first station list and the Weather tab sat empty. Keep the last snapshot and show it straight away, with its age;
 * the next broker message replaces it. Weather changes slowly, so a few hours
 * old is still useful, and the tab says how old it is. */
export const WX_SNAPSHOT_KEY = 'flysafe.wx.snapshot.v1';
export const WX_SNAPSHOT_MAX_AGE_MS = 6 * 60 * 60_000;
const WX_SAVE_EVERY_MS = 30_000;
let lastSaveMs = 0;

export function loadWxSnapshot(now = Date.now()): { stations: WxSensorRecord[]; at: number } | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(WX_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at?: unknown; stations?: unknown };
    if (typeof parsed.at !== 'number' || !Array.isArray(parsed.stations)) return null;
    if (now - parsed.at > WX_SNAPSHOT_MAX_AGE_MS || parsed.at > now + 60_000) return null;
    if (parsed.stations.length === 0) return null;
    return { stations: parsed.stations as WxSensorRecord[], at: parsed.at };
  } catch {
    return null;
  }
}

/** A station whose own observation time is older than the snapshot limit is dropped. */
const tooOld = (r: WxSensorRecord, now: number): boolean => {
  if (!r.observed_at) return false;
  const t = Date.parse(r.observed_at);
  return Number.isFinite(t) && now - t > WX_SNAPSHOT_MAX_AGE_MS;
};

function saveWxSnapshot(stations: WxSensorRecord[], at: number) {
  if (stations.length === 0 || at - lastSaveMs < WX_SAVE_EVERY_MS) return;
  lastSaveMs = at;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(WX_SNAPSHOT_KEY, JSON.stringify({ at, stations }));
    }
  } catch {
    /* storage full or blocked: the tab just waits for the broker as before */
  }
}

export interface WxSensorPoint {
  station_name: string;
  source: 'Alberta511' | 'Netatmo' | 'MSC';
  lat: number;
  lng: number;
  // Wind + temp are nullable. Netatmo basic stations frequently lack wind
  // sensors (WindSpeed/WindDirection arrive as null from v1); we still
  // want the pin on the map and the row in the list — popup just hides
  // the wind/temp lines when they're null. Dropping these stations was
  // why "Show on Map" rendered far fewer pins than the list showed.
  tempC: number | null;
  windSpdMph: number | null;
  windDirDeg: number | null;
  // Optional popup-enrichment fields (mockup `toggleWxSensorMap` body).
  // Pushed by the Weather tab when available; null otherwise. The popup
  // builder hides whichever rows are null.
  humidityPct?: number | null;
  pressureHpa?: number | null;
  location?: string | null;
  cameraUrl?: string | null;
}

interface WxStore {
  sensors: WxSensorPoint[];
  /** Full broker snapshot — every station the broker is publishing.
   *  Driven by MQTT `weather/sensors`. */
  allStations: WxSensorRecord[];
  /** When `allStations` was received (ms). A restored snapshot keeps its own time, so
   *  the tab can say how old the data is. Null until anything has arrived. */
  allStationsAt: number | null;
  /** Per-sensor map visibility (mockup `toggleWxSensorMap`). `null` = use
   * global `ui.showWxSensors`; otherwise overrides for that station. */
  perSensorOn: Record<string, boolean>;
  setSensors: (s: WxSensorPoint[]) => void;
  setAllStations: (s: WxSensorRecord[]) => void;
  toggleSensor: (stationName: string) => void;
  clear: () => void;
}

const restored = loadWxSnapshot();

export const useWxStore = create<WxStore>()(
  subscribeWithSelector((set, get) => ({
    sensors: [],
    allStations: restored?.stations ?? [],
    allStationsAt: restored?.at ?? null,
    perSensorOn: {},
    setSensors: (sensors) => set({ sensors }),
    setAllStations: (incoming) => {
      const at = Date.now();
      // Merge by station id, like the source does across the per-source batches:
      // the first live batch must not shrink a restored snapshot to one source.
      const byId = new Map(get().allStations.map((r) => [r.id, r]));
      for (const r of incoming) byId.set(r.id, r);
      const allStations = [...byId.values()].filter((r) => !tooOld(r, at));
      set({ allStations, allStationsAt: allStations.length > 0 ? at : null });
      saveWxSnapshot(allStations, at);
    },
    toggleSensor: (stationName) => set((s) => ({
      perSensorOn: { ...s.perSensorOn, [stationName]: !s.perSensorOn[stationName] },
    })),
    // `allStations` deliberately preserved across clear() — it's a global
    // broker snapshot, not a per-context selection.
    clear: () => set({ sensors: [], perSensorOn: {} }),
  })),
);
